/**
 * Wizard Memory Templates
 * Converts wizard form data into structured memories for the unified memory system
 */

import { generateEntityId } from './entityId';

/**
 * Templates for creating memories from wizard data
 * Based on coaching session approach covering:
 * - Parent info, child details, diagnosis timing
 * - Family situation, school context
 * - Current support status and needs assessment
 * - Priority areas for new families vs experienced ones
 */
const templates = {
  /**
   * Parent/User information
   */
  parentInfo: (data) => ({
    content: `Bruker (forelder) med barn som trenger støtte. Deltar i Coord for veiledning om støtteordninger.`,
    category: 'user_self',
    subcategory: 'identity',
    sensitivityLevel: 1,
    entityName: 'Bruker',
    // '__USER__' is resolved to the authenticated userId by the /from-wizard
    // backend handler — keeps the JWT sub claim out of frontend state.
    entityId: '__USER__',
    entityType: 'user',
    metadata: {
      extraction_method: 'onboarding_wizard',
      wizard_step: 'parent_info',
      timestamp: new Date().toISOString()
    }
  }),
  /**
   * Child basic information
   */
  childBasicInfo: (data) => ({
    content: data.alias 
      ? `Bruker har barn som heter ${data.alias}, født i ${data.birthYear}`
      : `Bruker har barn født i ${data.birthYear}`,
    category: 'family_members',
    subcategory: 'children',
    sensitivityLevel: 1,
    entityName: data.alias || 'Barn',
    entityId: data.alias ? generateEntityId(data.alias) : `child_${data.birthYear}`,
    entityType: 'child',
    metadata: {
      extraction_method: 'onboarding_wizard',
      wizard_step: 'child_basic_info',
      birth_year: data.birthYear,
      alias: data.alias
    }
  }),

  /**
   * Child diagnoses with timing context
   */
  diagnosis: (data) => {
    const diagnosisYear = data.diagnosisYear || data.diagnosisAge;
    const currentYear = new Date().getFullYear();
    const childAge = data.birthYear ? currentYear - data.birthYear : data.age;
    
    let content = data.alias 
      ? `${data.alias} har diagnose: ${data.diagnoses.join(', ')}`
      : `Barnet har diagnose: ${data.diagnoses.join(', ')}`;
    
    // Add timing context for coaching assessment
    if (diagnosisYear) {
      const yearsWithDiagnosis = currentYear - diagnosisYear;
      content += `. Diagnostisert ca. ${diagnosisYear} (${yearsWithDiagnosis} år siden)`;
      
      // Assess if family is new to the process
      if (yearsWithDiagnosis <= 2) {
        content += ` - relativt ny diagnose, familie kan være nye i støttesystemet`;
      } else {
        content += ` - har erfaring med diagnosen over tid`;
      }
    } else if (data.diagnosisAge) {
      content += `. Diagnostisert som ${data.diagnosisAge}-åring`;
      const yearsWithDiagnosis = childAge - data.diagnosisAge;
      if (yearsWithDiagnosis <= 2) {
        content += ` - relativt ny diagnose`;
      }
    }
    
    return {
      content,
      category: 'family_members',
      subcategory: 'children',
      sensitivityLevel: 3, // Medical information is highly sensitive
      entityName: data.alias || 'Barn',
      entityId: data.alias ? generateEntityId(data.alias) : `child_${data.birthYear}`,
      entityType: 'child',
      metadata: {
        extraction_method: 'onboarding_wizard',
        wizard_step: 'child_diagnosis',
        diagnoses: data.diagnoses,
        birth_year: data.birthYear,
        diagnosis_year: diagnosisYear,
        experience_level: diagnosisYear && (currentYear - diagnosisYear) <= 2 ? 'new' : 'experienced'
      }
    };
  },

  /**
   * Family situation context
   */
  familySituation: (data) => ({
    content: `Familiesituasjon: ${data.familyStatus || 'ikke spesifisert'}${data.siblings ? `, har ${data.siblings} søsken` : ''}`,
    category: 'family_context',
    subcategory: 'family_situation',
    sensitivityLevel: 2,
    entityName: 'Familie',
    // Backend resolves 'household:__USER__' to 'household:${userId}'
    entityId: 'household:__USER__',
    entityType: 'household',
    metadata: {
      extraction_method: 'onboarding_wizard',
      wizard_step: 'family_situation',
      family_status: data.familyStatus,
      siblings: data.siblings
    }
  }),

  /**
   * Location information
   */
  location: (data) => ({
    content: `Familie bor i ${data.kommune} kommune`,
    category: 'location_info',
    subcategory: 'kommune',
    sensitivityLevel: 1,
    entityName: data.kommune,
    // Prefix disambiguates the kommune entity space from person names that
    // happen to normalize the same way.
    entityId: `kommune:${generateEntityId(data.kommune)}`,
    entityType: 'municipality',
    metadata: {
      extraction_method: 'onboarding_wizard',
      wizard_step: 'location',
      kommune: data.kommune
    }
  }),

  /**
   * School stage information
   */
  schoolStage: (data) => ({
    content: data.alias 
      ? `${data.alias} går på ${data.schoolStage.toLowerCase()}`
      : `Barnet går på ${data.schoolStage.toLowerCase()}`,
    category: 'family_members',
    subcategory: 'children',
    sensitivityLevel: 1,
    entityName: data.alias || 'Barn',
    entityId: data.alias ? generateEntityId(data.alias) : `child_${data.birthYear}`,
    entityType: 'child',
    metadata: {
      extraction_method: 'onboarding_wizard',
      wizard_step: 'school_stage',
      school_stage: data.schoolStage,
      birth_year: data.birthYear
    }
  }),

  /**
   * Child challenges
   */
  challenges: (data) => ({
    content: data.alias 
      ? `${data.alias} har utfordringer med: ${data.challenges.join(', ')}`
      : `Barnet har utfordringer med: ${data.challenges.join(', ')}`,
    category: 'family_members',
    subcategory: 'children',
    sensitivityLevel: 2,
    entityName: data.alias || 'Barn',
    entityId: data.alias ? generateEntityId(data.alias) : `child_${data.birthYear}`,
    entityType: 'child',
    metadata: {
      extraction_method: 'onboarding_wizard',
      wizard_step: 'challenges',
      challenges: data.challenges,
      birth_year: data.birthYear
    }
  }),

  /**
   * Current benefits with priority assessment
   */
  currentBenefits: (data) => {
    const hasADHD = data.diagnoses?.some(d => d.toLowerCase().includes('adhd'));
    const hasAutism = data.diagnoses?.some(d => d.toLowerCase().includes('autisme') || d.toLowerCase().includes('autism'));
    const isNewToSystem = data.experienceLevel === 'new';
    
    let content = '';
    let priorityRecommendations = [];
    
    if (data.currentBenefits.includes('Ingen av disse')) {
      content = `Familie mottar ingen støtteordninger i dag`;
      
      // Priority recommendations for new families
      if (isNewToSystem) {
        priorityRecommendations = ['hjelpestønad', 'grunnstønad'];
        if (hasADHD) {
          priorityRecommendations.push('grunnstønad (ADHD - enklere dokumentasjon)');
        }
        priorityRecommendations.push('ledsagerbevis', 'avlastning', 'støttekontakt');
        
        if (hasAutism && data.needsIntensiveSupport) {
          priorityRecommendations.push('pleiepenger', 'BPA');
        }
        
        content += `. Anbefaling for nye familier: start med ${priorityRecommendations.slice(0, 3).join(', ')}`;
      }
    } else {
      content = `Familie mottar følgende støtteordninger: ${data.currentBenefits.join(', ')}`;
      
      // Assess what might be missing from standard checklist
      const hasBasicSupport = data.currentBenefits.some(b => 
        b.includes('Hjelpestønad') || b.includes('Grunnstønad')
      );
      
      if (!hasBasicSupport) {
        priorityRecommendations.push('vurder grunnleggende støtteordninger');
      }
    }
    
    return {
      content,
      category: 'benefits_journey',
      subcategory: 'current_status',
      sensitivityLevel: 2,
      entityName: 'Støtteordninger',
      entityId: data.currentBenefits.includes('Ingen av disse') ? 'current_benefits_none' : 'current_benefits',
      entityType: 'benefit_status',
      metadata: {
        extraction_method: 'onboarding_wizard',
        wizard_step: 'benefits',
        current_benefits: data.currentBenefits,
        priority_recommendations: priorityRecommendations,
        has_adhd: hasADHD,
        has_autism: hasAutism,
        is_new_to_system: isNewToSystem
      }
    };
  },

  /**
   * Current struggles and dreams (coaching approach)
   */
  currentStruggles: (data) => ({
    content: `Hva familien strever mest med akkurat nå: ${data.currentStruggles}`,
    category: 'family_context',
    subcategory: 'current_challenges',
    sensitivityLevel: 2,
    entityName: 'Familie',
    // Same household synthetic key as familySituation/familyGoals so
    // re-onboarding updates the existing record instead of duplicating.
    entityId: 'household:__USER__',
    entityType: 'household',
    metadata: {
      extraction_method: 'onboarding_wizard',
      wizard_step: 'current_needs',
      struggles: data.currentStruggles
    }
  }),

  /**
   * Family dreams and goals
   */
  familyGoals: (data) => ({
    content: `Hva familien drømmer om/skulle ønske i stedet: ${data.familyGoals}`,
    category: 'family_context',
    subcategory: 'goals',
    sensitivityLevel: 1,
    entityName: 'Familie',
    // Same household synthetic key as familySituation/currentStruggles.
    entityId: 'household:__USER__',
    entityType: 'household',
    metadata: {
      extraction_method: 'onboarding_wizard',
      wizard_step: 'current_needs',
      goals: data.familyGoals
    }
  }),

  /**
   * Intensive support needs assessment
   */
  intensiveSupportNeeds: (data) => ({
    content: `Behov for intensiv støtte: ${data.needsNightWatch ? 'nattevåk' : ''}${data.needsContinuousSupervision ? ', kontinuerlig tilsyn store deler av dagen' : ''}${data.needsIntensiveSupport ? ', omfattende hjelpebehov' : ''}`.replace(/^, /, ''),
    category: 'family_members',
    subcategory: 'support_needs',
    sensitivityLevel: 3,
    entityName: data.alias || 'Barn',
    entityId: data.alias ? generateEntityId(data.alias) : `child_${data.birthYear}`,
    entityType: 'child',
    metadata: {
      extraction_method: 'onboarding_wizard',
      wizard_step: 'intensive_needs',
      needs_night_watch: data.needsNightWatch,
      needs_continuous_supervision: data.needsContinuousSupervision,
      needs_intensive_support: data.needsIntensiveSupport
    }
  })
};

// generateEntityId moved to ./entityId.js (single source of truth).
// See coord-frontend/src/utils/entityId.js — twin at coord-backend/src/lib/entityId.js.

/**
 * Creates memories from wizard step data
 * @param {string} stepName - Name of the wizard step
 * @param {object} stepData - Data from the wizard step
 * @param {object} contextData - Additional context data (e.g., child info from previous steps)
 * @returns {array} - Array of memory objects to create
 */
export function createMemoriesFromWizardStep(stepName, stepData, contextData = {}) {
  const memories = [];
  const timestamp = new Date().toISOString();

  // Merge context data with step data
  const fullData = { ...contextData, ...stepData };

  switch (stepName) {
    case 'child':
      // Only record the applicant ("Bruker" / user_self) when the user
      // actually supplied applicant-context data on this step. Previously
      // this was unconditional, which produced a stale user_self record
      // for every user who started and skipped StepChild — see
      // docs/INVESTIGATIONS/phase3-taxonomy-readout.md finding #4.
      const hasApplicantSignal = !!(
        stepData.birthYear ||
        stepData.alias ||
        (stepData.diagnoses && stepData.diagnoses.length > 0) ||
        stepData.familyStatus ||
        stepData.siblings
      );

      if (hasApplicantSignal) {
        const parentInfo = templates.parentInfo(fullData);
        memories.push({
          ...parentInfo,
          source: {
            type: 'onboarding_wizard',
            step: 'child',
            created_at: timestamp
          }
        });
      }

      if (stepData.birthYear) {
        const basicInfo = templates.childBasicInfo(fullData);
        memories.push({
          ...basicInfo,
          source: {
            type: 'onboarding_wizard',
            step: 'child',
            created_at: timestamp
          }
        });
      }

      if (stepData.diagnoses && stepData.diagnoses.length > 0) {
        const diagnosisInfo = templates.diagnosis(fullData);
        memories.push({
          ...diagnosisInfo,
          source: {
            type: 'onboarding_wizard',
            step: 'child',
            created_at: timestamp
          }
        });
      }

      if (stepData.familyStatus || stepData.siblings) {
        const familyInfo = templates.familySituation(fullData);
        memories.push({
          ...familyInfo,
          source: {
            type: 'onboarding_wizard',
            step: 'child',
            created_at: timestamp
          }
        });
      }
      break;

    case 'location':
      if (stepData.kommune) {
        const locationInfo = templates.location(fullData);
        memories.push({
          ...locationInfo,
          source: {
            type: 'onboarding_wizard',
            step: 'location',
            created_at: timestamp
          }
        });
      }

      if (stepData.schoolStage) {
        const schoolInfo = templates.schoolStage(fullData);
        memories.push({
          ...schoolInfo,
          source: {
            type: 'onboarding_wizard',
            step: 'location',
            created_at: timestamp
          }
        });
      }
      break;

    case 'challenges':
      if (stepData.challenges && stepData.challenges.length > 0) {
        const challengesInfo = templates.challenges(fullData);
        memories.push({
          ...challengesInfo,
          source: {
            type: 'onboarding_wizard',
            step: 'challenges',
            created_at: timestamp
          }
        });
      }
      break;

    case 'benefits':
      if (stepData.currentBenefits && stepData.currentBenefits.length > 0) {
        const benefitsInfo = templates.currentBenefits(fullData);
        memories.push({
          ...benefitsInfo,
          source: {
            type: 'onboarding_wizard',
            step: 'benefits',
            created_at: timestamp
          }
        });
      }
      break;

    case 'current_needs':
      if (stepData.currentStruggles) {
        const strugglesInfo = templates.currentStruggles(fullData);
        memories.push({
          ...strugglesInfo,
          source: {
            type: 'onboarding_wizard',
            step: 'current_needs',
            created_at: timestamp
          }
        });
      }

      if (stepData.familyGoals) {
        const goalsInfo = templates.familyGoals(fullData);
        memories.push({
          ...goalsInfo,
          source: {
            type: 'onboarding_wizard',
            step: 'current_needs',
            created_at: timestamp
          }
        });
      }
      break;

    case 'intensive_needs':
      if (stepData.needsNightWatch || stepData.needsContinuousSupervision || stepData.needsIntensiveSupport) {
        const intensiveInfo = templates.intensiveSupportNeeds(fullData);
        memories.push({
          ...intensiveInfo,
          source: {
            type: 'onboarding_wizard',
            step: 'intensive_needs',
            created_at: timestamp
          }
        });
      }
      break;

    default:
      console.warn(`Unknown wizard step: ${stepName}`);
  }

  return memories;
}

/**
 * Creates a summary memory for completed wizard
 * @param {object} allWizardData - Complete wizard data
 * @returns {object} - Summary memory object
 */
export function createWizardSummaryMemory(allWizardData) {
  const timestamp = new Date().toISOString();
  let summaryContent = `Bruker fullførte onboarding-veiviser. `;
  
  if (allWizardData.child?.alias) {
    summaryContent += `Har barn som heter ${allWizardData.child.alias}`;
    if (allWizardData.child?.birthYear) {
      summaryContent += `, født i ${allWizardData.child.birthYear}`;
    }
    summaryContent += `. `;
  }
  
  if (allWizardData.kommune) {
    summaryContent += `Bor i ${allWizardData.kommune}. `;
  }
  
  if (allWizardData.challenges?.length > 0) {
    summaryContent += `Hovedutfordringer: ${allWizardData.challenges.join(', ')}. `;
  }
  
  if (allWizardData.currentBenefits?.length > 0) {
    if (allWizardData.currentBenefits.includes('Ingen av disse')) {
      summaryContent += `Mottar ingen støtteordninger.`;
    } else {
      summaryContent += `Mottar: ${allWizardData.currentBenefits.join(', ')}.`;
    }
  }

  return {
    content: summaryContent.trim(),
    category: 'benefits_journey',
    subcategory: 'applications',
    sensitivityLevel: 2,
    entityName: 'Onboarding',
    entityId: 'wizard_completion',
    entityType: 'milestone',
    source: {
      type: 'onboarding_wizard',
      step: 'completion',
      created_at: timestamp
    },
    metadata: {
      extraction_method: 'onboarding_wizard',
      wizard_step: 'completion',
      wizard_data: allWizardData,
      completed_at: timestamp
    }
  };
}

/**
 * Memory creation service function
 * @param {array} memories - Array of memory objects to create
 * @returns {Promise} - Promise that resolves when memories are created
 */
export async function createWizardMemories(memories) {
  const { authFetch } = await import('../utils/auth');
  
  if (!memories || memories.length === 0) {
    return { success: true, created: 0 };
  }

  try {
    const response = await authFetch('/memories/from-wizard', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        memories: memories
      })
    });

    if (response.ok) {
      const result = await response.json();
      return result;
    } else {
      console.error('Failed to create wizard memories:', await response.text());
      return { success: false, error: 'Failed to create memories' };
    }
  } catch (error) {
    console.error('Error creating wizard memories:', error);
    return { success: false, error: error.message };
  }
}

export default templates;